import {
  CalendarClock,
  Loader2,
  Printer,
  Scissors,
  XCircle,
} from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

import {
  type CarrierMovePreview,
  type CarrierPickupBooking,
  type CarrierPickupSlot,
  useCancelCarrierPickupBooking,
  useCarrierPickupSlots,
  useDetachCarrierPickupBookingShipment,
  usePrintCarrierPickupBooking,
  useRescheduleCarrierPickupBooking,
  useUpdateCarrierPickupBookingNotes,
} from "@/api/carrier";
import { DetailDrawer } from "@/components/detail-drawer";
import { humanizeStatus, StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { hasHttpStatus } from "@/lib/api-error";
import { formatDateOnly, formatDateTime } from "@/lib/format";
import {
  formatPickupBookingAddress,
  formatPickupBookingCustomerSummary,
} from "@/lib/pickup-booking-display";

interface PickupBookingDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: CarrierPickupBooking;
}

type BookingShipment = CarrierPickupBooking["shipments"][number];

export function PickupBookingDetailDialog({
  open,
  onOpenChange,
  booking,
}: PickupBookingDetailDialogProps) {
  const update = useUpdateCarrierPickupBookingNotes();
  const cancelBooking = useCancelCarrierPickupBooking();
  const detachShipment = useDetachCarrierPickupBookingShipment();
  const rescheduleBooking = useRescheduleCarrierPickupBooking();
  const printBooking = usePrintCarrierPickupBooking();
  const slots = useCarrierPickupSlots(booking.carrierAddOnService?.id, {
    dispatchCenterId: booking.dispatchCenter?.id,
  });
  const actionInFlight = useRef<string | null>(null);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [savedNotes, setSavedNotes] = useState(booking.notes ?? "");
  const [notes, setNotes] = useState(booking.notes ?? "");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [detachTarget, setDetachTarget] = useState<BookingShipment | null>(
    null
  );
  const [detachReason, setDetachReason] = useState("");
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [movePreview, setMovePreview] = useState<CarrierMovePreview | null>(
    null
  );
  const [rescheduleDate, setRescheduleDate] = useState(
    booking.scheduledDate.slice(0, 10)
  );
  const [rescheduleSlotId, setRescheduleSlotId] = useState(
    booking.slot?.id ?? ""
  );

  useEffect(() => {
    const nextNotes = booking.notes ?? "";
    setSavedNotes(nextNotes);
    setNotes(nextNotes);
    setRescheduleDate(booking.scheduledDate.slice(0, 10));
    setRescheduleSlotId(booking.slot?.id ?? "");
  }, [booking.notes, booking.scheduledDate, booking.slot?.id]);

  useEffect(() => {
    if (
      slots.data &&
      !slots.data.some((slot) => slot.id === rescheduleSlotId && slot.isActive)
    ) {
      setRescheduleSlotId(slots.data.find((slot) => slot.isActive)?.id ?? "");
    }
  }, [rescheduleSlotId, slots.data]);

  const dirty = savedNotes !== notes;
  const scheduled = booking.derivedState === "Scheduled";
  const canReschedule =
    scheduled &&
    !!booking.carrierAddOnService?.id &&
    !!booking.dispatchCenter?.id;
  const notesPending = activeAction === "notes" || update.isPending;
  const cancelPending = activeAction === "cancel" || cancelBooking.isPending;
  const detachPending = activeAction === "detach" || detachShipment.isPending;
  const reschedulePending =
    activeAction === "reschedule" || rescheduleBooking.isPending;
  const printPending = activeAction === "print" || printBooking.isPending;
  const busy =
    notesPending ||
    cancelPending ||
    detachPending ||
    reschedulePending ||
    printPending;
  const selectedSlotAvailable =
    !!rescheduleSlotId &&
    !!slots.data?.some((slot) => slot.id === rescheduleSlotId && slot.isActive);
  const cancelFormId = `cancel-pickup-booking-${booking.id}`;
  const detachFormId = `detach-pickup-shipment-${booking.id}`;
  const rescheduleFormId = `reschedule-pickup-booking-${booking.id}`;

  const beginAction = (key: string) => {
    if (actionInFlight.current !== null) return false;
    actionInFlight.current = key;
    setActiveAction(key);
    return true;
  };

  const finishAction = (key: string) => {
    if (actionInFlight.current === key) {
      actionInFlight.current = null;
      setActiveAction(null);
    }
  };

  const handlePrint = async () => {
    if (!beginAction("print")) return;
    let url: string | null = null;
    try {
      const result = await printBooking.mutateAsync({ id: booking.id });
      url = URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Pickup sheet downloaded.");
    } catch (error) {
      toast.error(
        hasHttpStatus(error, 403)
          ? "You do not have permission to print this pickup booking."
          : "Failed to print pickup booking."
      );
    } finally {
      if (url) URL.revokeObjectURL(url);
      finishAction("print");
    }
  };

  const handleSaveNotes = async () => {
    if (!dirty || !beginAction("notes")) return;
    const normalizedNotes = notes.trim() === "" ? "" : notes;
    try {
      await update.mutateAsync({
        id: booking.id,
        notes: normalizedNotes || null,
      });
      setSavedNotes(normalizedNotes);
      setNotes(normalizedNotes);
      toast.success("Pickup notes saved.");
    } catch (error) {
      toast.error(
        hasHttpStatus(error, 403)
          ? "You do not have permission to update pickup notes."
          : "Failed to save pickup notes."
      );
    } finally {
      finishAction("notes");
    }
  };

  const handleCancel = async (event: FormEvent) => {
    event.preventDefault();
    if (!beginAction("cancel")) return;
    try {
      await cancelBooking.mutateAsync({
        id: booking.id,
        reason: cancelReason.trim() || undefined,
        notes: notes.trim() === "" ? null : notes,
      });
      toast.success("Pickup booking cancelled.");
      setCancelOpen(false);
      onOpenChange(false);
    } catch (error) {
      toast.error(
        hasHttpStatus(error, 403)
          ? "You do not have permission to cancel this pickup booking."
          : "Failed to cancel pickup booking."
      );
    } finally {
      finishAction("cancel");
    }
  };

  const handleDetach = async (event: FormEvent) => {
    event.preventDefault();
    if (!detachTarget || !beginAction("detach")) return;
    try {
      await detachShipment.mutateAsync({
        bookingId: booking.id,
        shipmentId: detachTarget.id,
        reason: detachReason.trim() || undefined,
      });
      toast.success(`SHP-${detachTarget.shipmentNumber} detached.`);
      setDetachTarget(null);
      onOpenChange(false);
    } catch (error) {
      toast.error(
        hasHttpStatus(error, 403)
          ? "You do not have permission to detach this shipment."
          : "Failed to detach shipment."
      );
    } finally {
      finishAction("detach");
    }
  };

  const handleReschedule = async (event: FormEvent) => {
    event.preventDefault();
    if (
      !rescheduleDate ||
      !selectedSlotAvailable ||
      !beginAction("reschedule")
    ) {
      return;
    }
    try {
      const result = await rescheduleBooking.mutateAsync({
        id: booking.id,
        newScheduledDate: rescheduleDate,
        expectedPreviewFingerprint: movePreview?.previewFingerprint,
        acceptCurrentLocationConfiguration: Boolean(movePreview),
        newSlotId: rescheduleSlotId,
        shipmentIds: booking.shipments
          .filter((shipment) => shipment.status !== "CANCELLED")
          .map((shipment) => shipment.id),
      });
      if (result?.preview) {
        setMovePreview(result.preview);
        return;
      }
      setMovePreview(null);
      toast.success("Pickup booking rescheduled.");
      setRescheduleOpen(false);
      onOpenChange(false);
    } catch (error) {
      toast.error(
        hasHttpStatus(error, 403)
          ? "You do not have permission to reschedule this pickup booking."
          : "Failed to reschedule pickup booking."
      );
    } finally {
      finishAction("reschedule");
    }
  };

  return (
    <DetailDrawer
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && busy) return;
        onOpenChange(nextOpen);
      }}
      title="Pickup booking"
      description={`${formatDateOnly(booking.scheduledDate)} · ${humanizeStatus(
        booking.derivedState
      )}`}
      widthClassName="sm:max-w-2xl"
      closeDisabled={busy}
    >
      <div className="space-y-5 text-sm">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Button
            type="button"
            size="sm-touch"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={handlePrint}
            disabled={busy}
          >
            {printPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Printer className="mr-2 h-4 w-4" />
            )}
            Print
          </Button>
          <Button
            type="button"
            size="sm-touch"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={() => setRescheduleOpen(true)}
            disabled={!canReschedule || busy}
          >
            <CalendarClock className="mr-2 h-4 w-4" />
            Reschedule
          </Button>
          <Button
            type="button"
            size="sm-touch"
            variant="outline"
            className="text-destructive hover:text-destructive"
            onClick={() => setCancelOpen(true)}
            disabled={!scheduled || busy}
          >
            <XCircle className="mr-2 h-4 w-4" />
            Cancel booking
          </Button>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border p-4">
          <DetailField label="Booking ID" value={booking.id} mono />
          <DetailField
            label="State"
            value={<StatusBadge status={booking.derivedState} />}
          />
          <DetailField
            label="Date"
            value={formatDateOnly(booking.scheduledDate)}
          />
          <DetailField label="Time slot" value={formatSlot(booking)} />
          <DetailField
            label="Pickup service"
            value={
              booking.carrierAddOnService
                ? `${booking.carrierAddOnService.name} (${booking.carrierAddOnService.code})`
                : "Unavailable"
            }
          />
          <DetailField
            label="Dispatch center"
            value={
              booking.dispatchCenter
                ? `${booking.dispatchCenter.name} (${booking.dispatchCenter.code})`
                : "Unavailable"
            }
          />
          <DetailField
            label="Customer"
            value={formatPickupBookingCustomerSummary(booking)}
          />
          <DetailField
            label="Customer contact"
            value={booking.placerCustomer?.email ?? "Unavailable"}
          />
          <DetailField
            label="Address"
            value={formatPickupBookingAddress(booking)}
            className="col-span-2"
          />
          {booking.cancelledAt && (
            <DetailField
              label="Cancelled"
              value={formatDateTime(booking.cancelledAt)}
              className="col-span-2"
            />
          )}
        </dl>

        <section
          aria-labelledby="booking-shipments-heading"
          className="space-y-2"
        >
          <h3
            id="booking-shipments-heading"
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          >
            Shipments ({booking.attachedShipmentCount} active /{" "}
            {booking.totalShipmentCount} total)
          </h3>
          {booking.shipments.length === 0 ? (
            <p className="rounded-lg border p-4 text-sm text-muted-foreground">
              No shipments attached.
            </p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {booking.shipments.map((shipment) => (
                <li key={shipment.id} className="space-y-1.5 p-3">
                  <div className="flex min-h-11 items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-mono text-xs">
                        SHP-{shipment.shipmentNumber}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {shipment.trackingNumber ?? "Tracking unavailable"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={shipment.status} />
                      {scheduled && canDetachShipment(shipment) && (
                        <Button
                          type="button"
                          size="sm-touch"
                          variant="ghost"
                          onClick={() => setDetachTarget(shipment)}
                          disabled={busy}
                        >
                          <Scissors className="mr-2 h-3.5 w-3.5" />
                          Detach
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Pickup tasks: {formatTaskStates(shipment)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="space-y-2">
          <label
            id="booking-notes-heading"
            htmlFor="pb-notes"
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          >
            Notes
          </label>
          <Textarea
            id="pb-notes"
            className="min-h-24"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Operational notes (gate code, contact, instructions)..."
            disabled={notesPending}
          />
          <div className="flex items-center justify-end gap-2">
            {update.isError && (
              <span role="alert" className="text-xs text-destructive">
                Notes were not saved. Your changes are preserved.
              </span>
            )}
            <Button
              type="button"
              size="sm-touch"
              disabled={!dirty || busy}
              onClick={handleSaveNotes}
            >
              {notesPending ? "Saving..." : "Save notes"}
            </Button>
          </div>
        </section>
      </div>

      <Dialog
        open={cancelOpen}
        onOpenChange={(next) => {
          if (!cancelPending) setCancelOpen(next);
        }}
        title="Cancel pickup booking"
        presentation="responsive-sheet"
        closeDisabled={cancelPending}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              size="touch"
              onClick={() => setCancelOpen(false)}
              disabled={cancelPending}
            >
              Keep booking
            </Button>
            <Button
              type="submit"
              form={cancelFormId}
              variant="destructive"
              size="touch"
              disabled={cancelPending}
            >
              {cancelPending ? "Cancelling..." : "Cancel booking"}
            </Button>
          </div>
        }
      >
        <form
          id={cancelFormId}
          onSubmit={handleCancel}
          className="space-y-4 text-sm"
          aria-busy={cancelPending || undefined}
        >
          <p className="text-muted-foreground">
            This removes the booking from scheduled pickup work. Add a reason
            when it will help the operations team.
          </p>
          <div>
            <label htmlFor="cancel-reason" className="mb-1 block font-medium">
              Reason
            </label>
            <Textarea
              id="cancel-reason"
              className="min-h-24"
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              disabled={cancelPending}
            />
          </div>
        </form>
      </Dialog>

      <Dialog
        open={detachTarget !== null}
        onOpenChange={(next) => {
          if (!next && !detachPending) setDetachTarget(null);
        }}
        title={
          detachTarget ? `Detach SHP-${detachTarget.shipmentNumber}` : "Detach"
        }
        presentation="responsive-sheet"
        closeDisabled={detachPending}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              size="touch"
              onClick={() => setDetachTarget(null)}
              disabled={detachPending}
            >
              Keep shipment
            </Button>
            <Button
              type="submit"
              form={detachFormId}
              variant="destructive"
              size="touch"
              disabled={detachPending}
            >
              {detachPending ? "Detaching..." : "Detach shipment"}
            </Button>
          </div>
        }
      >
        <form
          id={detachFormId}
          onSubmit={handleDetach}
          className="space-y-4 text-sm"
          aria-busy={detachPending || undefined}
        >
          <p className="text-muted-foreground">
            The shipment will no longer be collected with this pickup booking.
          </p>
          <div>
            <label htmlFor="detach-reason" className="mb-1 block font-medium">
              Reason
            </label>
            <Textarea
              id="detach-reason"
              className="min-h-24"
              value={detachReason}
              onChange={(event) => setDetachReason(event.target.value)}
              disabled={detachPending}
            />
          </div>
        </form>
      </Dialog>

      <Dialog
        open={rescheduleOpen}
        onOpenChange={(next) => {
          if (!reschedulePending) setRescheduleOpen(next);
        }}
        title="Reschedule pickup"
        presentation="responsive-sheet"
        closeDisabled={reschedulePending}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              size="touch"
              onClick={() => setRescheduleOpen(false)}
              disabled={reschedulePending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={rescheduleFormId}
              size="touch"
              disabled={
                reschedulePending ||
                slots.isLoading ||
                slots.isError ||
                !rescheduleDate ||
                !selectedSlotAvailable
              }
            >
              {reschedulePending
                ? "Working..."
                : movePreview
                  ? "Confirm dates and facilities"
                  : "Review move"}
            </Button>
          </div>
        }
      >
        <form
          id={rescheduleFormId}
          onSubmit={handleReschedule}
          className="space-y-4 text-sm"
          aria-busy={reschedulePending || undefined}
        >
          {movePreview && (
            <div className="rounded border p-3">
              {movePreview.deliveryOutcomes.map((outcome) => (
                <div key={outcome.shipmentId}>
                  <p>
                    {outcome.deliveredPrecision === "DATE"
                      ? outcome.deliveredFastest.slice(0, 10)
                      : new Date(outcome.deliveredFastest).toLocaleString(
                          undefined,
                          { timeZone: outcome.timeZone }
                        )}{" "}
                    –{" "}
                    {outcome.deliveredPrecision === "DATE"
                      ? outcome.deliveredSlowest.slice(0, 10)
                      : new Date(outcome.deliveredSlowest).toLocaleString(
                          undefined,
                          { timeZone: outcome.timeZone }
                        )}{" "}
                    ({outcome.timeZone})
                  </p>
                  {outcome.locationSummary && (
                    <p>
                      Confirm facilities: {outcome.locationSummary.dispatch}.
                      Carrier handoff:{" "}
                      {outcome.locationSummary.dropoff ?? "No carrier counter"}.
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
          <div>
            <label htmlFor="reschedule-date" className="mb-1 block font-medium">
              Date
            </label>
            <Input
              id="reschedule-date"
              type="date"
              value={rescheduleDate}
              onChange={(event) => {
                setRescheduleDate(event.target.value);
                setMovePreview(null);
              }}
              disabled={reschedulePending}
              required
            />
          </div>
          <div>
            <label htmlFor="reschedule-slot" className="mb-1 block font-medium">
              Time slot
            </label>
            <NativeSelect
              id="reschedule-slot"
              value={rescheduleSlotId}
              onChange={(event) => {
                setRescheduleSlotId(event.target.value);
                setMovePreview(null);
              }}
              disabled={reschedulePending || slots.isLoading}
              required
            >
              <option value="" disabled>
                Select a time slot
              </option>
              {slots.data
                ?.filter((slot) => slot.isActive)
                .map((slot) => (
                  <option key={slot.id} value={slot.id}>
                    {slotLabel(slot)}
                  </option>
                ))}
            </NativeSelect>
            {slots.isLoading && (
              <p role="status" className="mt-2 text-xs text-muted-foreground">
                Loading pickup slots...
              </p>
            )}
            {slots.isError && (
              <div
                role="alert"
                className="mt-2 flex flex-wrap items-center gap-2 text-xs text-destructive"
              >
                <span>Pickup slots are unavailable.</span>
                <Button
                  type="button"
                  size="sm-touch"
                  variant="outline"
                  onClick={() => slots.refetch()}
                >
                  Retry
                </Button>
              </div>
            )}
            {!slots.isLoading &&
              !slots.isError &&
              !slots.data?.some((slot) => slot.isActive) && (
                <p className="mt-2 text-xs text-muted-foreground">
                  No active pickup slots are available.
                </p>
              )}
          </div>
        </form>
      </Dialog>
    </DetailDrawer>
  );
}

function DetailField({
  label,
  value,
  mono = false,
  className = "",
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className={`mt-1 break-words ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </dd>
    </div>
  );
}

function formatSlot(booking: CarrierPickupBooking): string {
  return booking.slot
    ? `${booking.slot.name} (${booking.slot.startTime}–${booking.slot.endTime})`
    : "Unavailable";
}

function formatTaskStates(shipment: BookingShipment): string {
  return shipment.tasks.length > 0
    ? shipment.tasks.map((task) => humanizeStatus(task.status)).join(", ")
    : "None";
}

function slotLabel(slot: CarrierPickupSlot): string {
  return `${slot.name} (${slot.startTime}–${slot.endTime})`;
}

function canDetachShipment(shipment: BookingShipment): boolean {
  const locked = new Set(["IN_PROGRESS", "COMPLETED", "FAILED"]);
  return !shipment.tasks.some((task) => locked.has(task.status));
}
